/**
 * Pure helpers for building dashboard click-through URLs.
 * Kept outside the component tree so they are trivially testable and never
 * force a re-render because of identity churn.
 */

// All metrics that require an explicit period range (Sofia dates).
const PERIOD_METRICS = new Set([
  "sent_cohort",
  "opened_cohort",
  "responded_cohort",
  "accepted_cohort",
  "declined_cohort",
  "accepted_value_period",
  "accepted_bucket",
]);

/**
 * Build the query string for /quotations click-through given a metric and
 * the active dashboard range. For accepted_bucket the caller passes the
 * per-bucket ``period_start`` / ``period_end`` in ``extras``.
 *
 * ``extras.owner`` (optional) forwards the active dashboard owner filter to
 * the quotations list so click-through counts match the KPI values under
 * every combination of filters.
 *
 * ``extras.team`` (optional, Phase 2D.3B.3) forwards the active dashboard TEAM
 * scope the same way. Unlike the owner filter it is forwarded for every metric
 * including ``unassigned_current``: a quotation with no responsible person still
 * belongs to a team scope („Без екип" when it has none), so dropping the team
 * would show rows the KPI never counted.
 *
 * @returns {URLSearchParams} always a fresh instance (safe to mutate/serialize)
 */
export function buildClickThroughParams(metric, { from, to } = {}, extras = {}) {
  const qs = new URLSearchParams();
  if (!metric) return qs;
  qs.set("dashboard_metric", metric);
  if (PERIOD_METRICS.has(metric)) {
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
  }
  if (extras.period_start) qs.set("period_start", extras.period_start);
  if (extras.period_end) qs.set("period_end", extras.period_end);
  // Owner filter passes through when supplied; the ``unassigned_current``
  // metric is always org-wide-unassigned and must NOT carry an owner filter.
  if (extras.owner && metric !== "unassigned_current") {
    qs.set("owner_id", extras.owner);
  }
  if (extras.team) {
    qs.set("team_id", extras.team);
  }
  return qs;
}

export function buildClickThroughUrl(metric, range, extras) {
  const qs = buildClickThroughParams(metric, range, extras);
  const s = qs.toString();
  return s ? `/quotations?${s}` : "/quotations";
}
